import React, { useMemo } from 'react';
import { formatCurrency } from '../../utils/currency.ts';

export interface PieSlice {
  key: string;
  label: string;
  color: string;
  value: number;
  percent: number;
}

interface Props {
  slices: PieSlice[];
  total: number;
  empty?: boolean;
}

/** Donut pie with legend (% + R$) — accent green-600. */
const PortfolioPieChart: React.FC<Props> = ({ slices, total, empty }) => {
  const paths = useMemo(() => {
    const r = 70;
    const circ = 2 * Math.PI * r;
    if (!slices.length || total <= 0) {
      return [
        {
          key: 'empty',
          color: '#e2e8f0',
          dash: `${circ} ${circ}`,
          offset: 0,
        },
      ];
    }
    let offset = 0;
    return slices.map(s => {
      const len = (Math.max(s.percent, 0) / 100) * circ;
      const item = {
        key: s.key,
        color: s.color,
        dash: `${len} ${circ - len}`,
        offset: -offset,
      };
      offset += len;
      return item;
    });
  }, [slices, total]);

  const totalLabel =
    total >= 1_000_000
      ? `R$ ${(total / 1_000_000).toFixed(2).replace('.', ',')}M`
      : formatCurrency(total, 'BRL', 'pt-BR');

  return (
    <div className="chart-body">
      <div className="pie-wrap" aria-label="Posição por categoria">
        <svg viewBox="0 0 168 168" width={168} height={168}>
          {paths.map(p => (
            <circle
              key={p.key}
              cx={84}
              cy={84}
              r={70}
              fill="none"
              stroke={p.color}
              strokeWidth={28}
              strokeDasharray={p.dash}
              strokeDashoffset={p.offset}
              transform="rotate(-90 84 84)"
            />
          ))}
          <circle cx={84} cy={84} r={48} fill="#fff" />
        </svg>
        <div className="pie-center">
          <div className="pie-total-label">Total</div>
          <div className="pie-total-value">{empty ? '—' : totalLabel}</div>
        </div>
      </div>
      <div className="pie-legend">
        {empty || slices.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>
            Sem posições ativas para montar a pizza.
          </div>
        ) : (
          slices.map(s => (
            <div key={s.key} className="pie-legend-item">
              <span className="pie-swatch" style={{ background: s.color }} />
              <span className="pie-legend-name">{s.label}</span>
              <span className="pie-legend-meta">
                <strong>{s.percent.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%</strong>
                {formatCurrency(s.value, 'BRL', 'pt-BR')}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PortfolioPieChart;
